// Dashboard sections: saved GitHub search queries, customizable per browser.

export interface Section {
  title: string;
  queries: string[];
  /** Keep only PRs whose attention set includes the viewer. */
  attention?: boolean;
}

/**
 * The pull requests waiting on the viewer: their own, and others' assigned
 * to them. A review asked of them without an assignment isn't their turn
 * (ALSO_INVOLVED_SECTION lists those).
 */
export const ATTENTION_SECTION: Section = {
  title: 'Needs your attention',
  queries: ['is:pr is:open author:@me archived:false', 'is:pr is:open assignee:@me -author:@me archived:false'],
  attention: true,
};

/** Others' pull requests assigned to the viewer; those whose review is asked of them too come first (logic/involvement.ts `byInvolvement`). */
export const INCOMING_SECTION: Section = {
  title: 'Assigned to you',
  queries: ['is:pr is:open assignee:@me -author:@me archived:false'],
};

/** The viewer's own open pull requests. */
export const OUTGOING_SECTION: Section = { title: 'Outgoing', queries: ['is:pr is:open author:@me archived:false'] };

/**
 * Others' pull requests that involve the viewer without being assigned to
 * them: a review asked of them (or of a team of theirs), or one they gave.
 * Worth knowing about, not their turn: below their own.
 */
export const ALSO_INVOLVED_SECTION: Section = {
  title: 'Also involved',
  queries: [
    'is:pr is:open review-requested:@me -assignee:@me -author:@me archived:false',
    'is:pr is:open reviewed-by:@me -assignee:@me -author:@me archived:false',
  ],
};

export const DEFAULT_SECTIONS: Section[] = [
  ATTENTION_SECTION,
  INCOMING_SECTION,
  OUTGOING_SECTION,
  ALSO_INVOLVED_SECTION,
  { title: 'Mentioned', queries: ['is:pr is:open mentions:@me archived:false'] },
  { title: 'Recently closed', queries: ['is:pr is:closed involves:@me closed:>={7d}'] },
];

// Defaults as they were: a stored list holding one unedited gets what
// replaced it (`upgradeSections`).
const ATTENTION_BEFORE: Section = { title: 'Needs your attention', queries: ['is:pr is:open involves:@me archived:false'], attention: true };
const INCOMING_BEFORE: Section[] = [
  { title: 'Incoming reviews', queries: ['is:pr is:open review-requested:@me archived:false', 'is:pr is:open reviewed-by:@me -author:@me archived:false'] },
  {
    title: 'Incoming reviews',
    queries: ['is:pr is:open review-requested:@me archived:false', 'is:pr is:open assignee:@me -author:@me archived:false', 'is:pr is:open reviewed-by:@me -author:@me archived:false'],
  },
];
const sameSection = (a: Section, b: Section) =>
  a.title === b.title && !!a.attention === !!b.attention && a.queries.length === b.queries.length && a.queries.every((q, i) => q === b.queries[i]);

/**
 * `sections` with the defaults since changed brought up to date where they
 * are just as they were; edited ones stay as they are. An old Incoming
 * reviews becomes Assigned to you, and what else it listed (reviews asked
 * without an assignment, and given) goes to Also involved, added after
 * Outgoing (else after Assigned to you) unless a section has that name.
 */
export function upgradeSections(sections: Section[]): Section[] {
  let incoming = false;
  const out = sections.map((s) => {
    if (sameSection(s, ATTENTION_BEFORE)) return ATTENTION_SECTION;
    if (!INCOMING_BEFORE.some((b) => sameSection(s, b))) return s;
    incoming = true;
    return INCOMING_SECTION;
  });
  if (!incoming || out.some((s) => s.title === ALSO_INVOLVED_SECTION.title)) return out;
  const outgoing = out.findIndex((s) => sameSection(s, OUTGOING_SECTION));
  const at = (outgoing >= 0 ? outgoing : out.indexOf(INCOMING_SECTION)) + 1;
  return [...out.slice(0, at), ALSO_INVOLVED_SECTION, ...out.slice(at)];
}

/** localStorage key of the customized sections (per user: cleared at sign-out). */
export const SECTIONS_KEY = 'changelist:dashboard-sections';
const KEY = SECTIONS_KEY;

export function expandQuery(q: string, now = new Date()): string {
  return q.replace(/\{(\d+)d\}/g, (_, n) => {
    const d = new Date(now.getTime() - Number(n) * 86400000);
    return d.toISOString().slice(0, 10);
  });
}

/**
 * A section query as searched: most recently updated first (GitHub's default
 * is best match), unless the query names its own `sort:`. Only the first page
 * of hits is read, so the order decides which pull requests are seen.
 */
export function searchQuery(q: string, now = new Date()): string {
  const expanded = expandQuery(q, now);
  return /(^|\s)sort:/.test(expanded) ? expanded : `${expanded} sort:updated-desc`;
}

export function formatSections(sections: Section[]): string {
  return sections.map((s) => `${s.attention ? '!' : ''}${s.title} = ${s.queries.join(' || ')}`).join('\n');
}

export function parseSections(text: string): Section[] {
  const out: Section[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const eq = line.indexOf('=');
    if (!line || eq < 0) continue;
    let title = line.slice(0, eq).trim();
    const attention = title.startsWith('!');
    if (attention) title = title.slice(1).trim();
    const queries = line
      .slice(eq + 1)
      .split('||')
      .map((q) => q.trim())
      .filter(Boolean);
    if (title && queries.length) out.push({ title, queries, ...(attention ? { attention } : {}) });
  }
  return out;
}

export function loadSections(): Section[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw) as Section[];
      if (Array.isArray(v) && v.length) return upgradeSections(v);
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_SECTIONS;
}

export function saveSections(sections: Section[] | null) {
  try {
    if (sections) localStorage.setItem(KEY, JSON.stringify(sections));
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The part of a section that could not load, as the noun phrase a failure
 * is explained for (`explain` in api/failure.ts): "18 of this section's
 * matching pull requests". The dashboard and the extension both use it.
 */
export function partialWhat(count: number): string {
  return `${count} of this section's matching pull requests`;
}

/**
 * An attention section's note on the rows it holds back while their
 * attention is worked out again (a saved copy's rows that changed since,
 * by the viewer or anyone), or, when that check failed, that they weren't.
 */
export function checkingWhat(count: number, failed: boolean): string {
  const prs = `${count} pull request${count === 1 ? '' : 's'}`;
  return failed ? `${prs} ${count === 1 ? "wasn't" : "weren't"} checked again` : `Checking ${prs} again…`;
}
